import { AuthProvider, Requester, RequesterResponse } from "./domain";
import {
  AtcEndpoint,
  AuditEndpoint,
  BankEndpoint,
  BillEndpoint,
  BrandEndpoint,
  CashRegisterEndpoint,
  ClientEndpoint,
  CompanyEndpoint,
  ContractEndpoint,
  ContractPolicyEndpoint,
  CountryEndpoint,
  CurrencyEndpoint,
  DeliveryEndpoint,
  DisbursementEndpoint,
  DisbursementReasonEndpoint,
  EMECeFEndpoint,
  FormEndpoint,
  InnEndpoint,
  MovementEndpoint,
  OrderEndpoint,
  OrderPreparationEndpoint,
  OrderRecordEndpoint,
  OrganizationEndpoint,
  PackagingEndpoint,
  PersonEndpoint,
  PharmacyEndpoint,
  PharmacyProductEndpoint,
  ProductCategoriesEndpoint,
  ProductEndpoint,
  ProductViewEndpoint,
  ReportEndpoint,
  RoleEndpoint,
  SaleEndpoint,
  StockExportEndpoint,
  SupplierEndpoint,
  TokensEndpoint,
  UnitEndpoint,
  UsageEndpoint,
  UserEndpoint,
  WholesalerBillStatementEndpoint,
  WholesalerClaimsEndpoint,
  WholesalerEndpoint,
  WholesalerProductEndpoint,
} from "./endpoints";
import { ProductHealthInsuranceEndpoint } from "./endpoints/ProductHealthInsuranceEndpoint";
import { TagsEndpoint } from "./endpoints/TagsEndpoint";

export interface ClientOptions {
  authProvider?: AuthProvider;
  log?: (...message: string[]) => void;
  requester?: Requester;
}

export class ApiClient {
  readonly atcs = new AtcEndpoint(this);
  readonly banks = new BankEndpoint(this);
  readonly bills = new BillEndpoint(this);
  readonly brands = new BrandEndpoint(this);
  readonly cashRegisters = new CashRegisterEndpoint(this);
  readonly movements = new MovementEndpoint(this);
  readonly productCategories = new ProductCategoriesEndpoint(this);
  readonly productHealthInsurance = new ProductHealthInsuranceEndpoint(this);
  readonly clients = new ClientEndpoint(this);
  readonly companies = new CompanyEndpoint(this);
  readonly contracts = new ContractEndpoint(this);
  readonly contractPolicies = new ContractPolicyEndpoint(this);
  readonly countries = new CountryEndpoint(this);
  readonly currencies = new CurrencyEndpoint(this);
  readonly disbursementReasons = new DisbursementReasonEndpoint(this);
  readonly disbursement = new DisbursementEndpoint(this);
  readonly deliveries = new DeliveryEndpoint(this);
  readonly forms = new FormEndpoint(this);
  readonly inns = new InnEndpoint(this);
  readonly packagings = new PackagingEndpoint(this);
  readonly persons = new PersonEndpoint(this);
  readonly pharmacies = new PharmacyEndpoint(this);
  readonly pharmacyProducts = new PharmacyProductEndpoint(this);
  readonly products = new ProductEndpoint(this);
  readonly productViews = new ProductViewEndpoint(this);
  readonly reports = new ReportEndpoint(this);
  readonly roles = new RoleEndpoint(this);
  readonly sales = new SaleEndpoint(this);
  readonly stockExports = new StockExportEndpoint(this);
  readonly suppliers = new SupplierEndpoint(this);
  readonly tags = new TagsEndpoint(this);
  readonly tokens = new TokensEndpoint(this);
  readonly units = new UnitEndpoint(this);
  readonly usage = new UsageEndpoint(this);
  readonly users = new UserEndpoint(this);
  readonly wholesalers = new WholesalerEndpoint(this);
  readonly wholesalerProducts = new WholesalerProductEndpoint(this);
  readonly orders = new OrderEndpoint(this);
  readonly orderRecords = new OrderRecordEndpoint(this);
  readonly orderPreparation = new OrderPreparationEndpoint(this);
  readonly organizations = new OrganizationEndpoint(this);
  readonly audit = new AuditEndpoint(this);
  readonly eMecef = new EMECeFEndpoint(this);
  readonly wholesalerClaims = new WholesalerClaimsEndpoint(this);
  readonly wholesalerBillStatement = new WholesalerBillStatementEndpoint(this);

  readonly requester: Requester;

  constructor(
    readonly apiUrl: string,
    { requester = fetch, authProvider, log }: ClientOptions = {},
  ) {
    this.requester = async (url, init = {}) => {
      if (authProvider != null) {
        const token = await authProvider(url, init);
        if (token != null) {
          const withPrefix = `Bearer ${token}`;
          if (init.headers == null) {
            init.headers = { Authorization: withPrefix };
          } else {
            init.headers.Authorization = withPrefix;
          }
        }
      }
      log?.(`[${init.method ?? "GET"}] ${url}`);
      return requester(url, init).then((res): RequesterResponse => {
        // no content
        if (res.status === 204) {
          return {
            status: res.status,
            json: () => Promise.resolve(""),
            blob: () => res.blob(),
          };
        }
        return {
          status: res.status,
          json: () => res.json(),
          blob: () => res.blob(),
        };
      });
    };
  }
}
